import React, { useEffect, useState } from 'react';
import { superApi, useSuperAdminAuth } from './SuperAdminAuth';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash2, RefreshCw, Copy, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Platform Domain Authentication
 *
 * Manages SES SPF / DKIM / DMARC for the Opswarex platform's OWN sending
 * domains (e.g. opswarex.com). Scoped server-side to
 * `company_id = "__platform__"` so no tenant ever sees or touches these
 * rows, and no tenant domain appears here.
 *
 * Backend: /api/super/email/domain-auth/* (see super_admin_email.py).
 * Reuses the same `email_domains` Mongo collection + SES helpers, just
 * pinned to the platform sentinel.
 */
export const SuperDomainAuthPage = () => {
  const { hasPerm } = useSuperAdminAuth();
  const canManage = hasPerm('super.email.manage');

  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ domain: '', sending_subdomain: '', reply_to_email: '' });
  const [saving, setSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await superApi.get('/super/email/domain-auth');
      setDomains(r.data.domains || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addDomain = async () => {
    if (!form.domain.trim()) return toast.error('Domain is required');
    setSaving(true);
    try {
      await superApi.post('/super/email/domain-auth', {
        domain: form.domain.trim().toLowerCase(),
        sending_subdomain: form.sending_subdomain.trim() || null,
        reply_to_email: form.reply_to_email.trim() || null,
      });
      toast.success('Domain added — set DNS records, then click Verify');
      setForm({ domain: '', sending_subdomain: '', reply_to_email: '' });
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const verifyDomain = async (id) => {
    setVerifyingId(id);
    try {
      const r = await superApi.post(`/super/email/domain-auth/${id}/verify`);
      const st = r.data.status;
      if (st === 'verified') toast.success('All records verified ✅');
      else if (st === 'partially_verified') toast.message('SES verified — DNS records still pending');
      else toast.message('Still pending — DNS may need more time to propagate');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifyingId(null);
    }
  };

  const deleteDomain = async (id, name) => {
    if (!window.confirm(`Remove ${name} from platform domains?`)) return;
    try {
      await superApi.delete(`/super/email/domain-auth/${id}`);
      toast.success('Domain removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const copy = (val) => {
    navigator.clipboard.writeText(val).then(() => toast.success('Copied'));
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-400" /> Platform Domain Auth
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            SPF / DKIM / DMARC for domains that send <strong>platform</strong> emails (password resets, billing,
            broadcasts). Completely isolated from tenant domains.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium"
            data-testid="super-domain-add-btn"
          >
            <Plus className="w-4 h-4" /> Add Domain
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : domains.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 mb-1">No platform domains yet</p>
          <p className="text-xs text-slate-500 mb-4">
            Add a sending domain (e.g. <code>opswarex.com</code>) to enable authenticated platform email.
          </p>
          {canManage && (
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium"
            >
              Add your first domain
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              canManage={canManage}
              onVerify={() => verifyDomain(d.id)}
              verifying={verifyingId === d.id}
              onDelete={() => deleteDomain(d.id, d.domain)}
              onCopy={copy}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddDialog
          form={form}
          setForm={setForm}
          onClose={() => setShowAdd(false)}
          onSave={addDomain}
          saving={saving}
        />
      )}
    </div>
  );
};

const DomainCard = ({ domain, canManage, onVerify, verifying, onDelete, onCopy }) => {
  const dns = domain.dns_records || {};
  const status = domain.status || 'pending_verification';
  const statusColor = status === 'verified'
    ? 'text-green-400 bg-green-500/10 border-green-500/30'
    : status === 'partially_verified'
      ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
      : 'text-slate-400 bg-slate-800/50 border-slate-700';

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid={`super-domain-${domain.domain}`}>
      <div className="p-5 flex items-start justify-between border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg font-medium text-white">{domain.domain}</span>
            <span className={`text-xs px-2 py-0.5 rounded-md border capitalize ${statusColor}`}>{status.replace('_', ' ')}</span>
          </div>
          <div className="text-xs text-slate-500">
            {domain.sending_subdomain && <>Sending: <code className="text-slate-300">{domain.sending_domain}</code> • </>}
            {domain.reply_to_email && <>Reply-to: <code className="text-slate-300">{domain.reply_to_email}</code></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onVerify}
            disabled={verifying}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-50"
            data-testid={`super-domain-verify-${domain.domain}`}
          >
            {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Verify
          </button>
          {canManage && (
            <button
              onClick={onDelete}
              className="p-1.5 rounded-md hover:bg-red-500/15 text-slate-400 hover:text-red-400"
              data-testid={`super-domain-delete-${domain.domain}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-5 space-y-3">
        {dns.verification && <DnsRow record={dns.verification} label="SES Verification" onCopy={onCopy} />}
        {dns.spf && <DnsRow record={dns.spf} label="SPF" onCopy={onCopy} />}
        {dns.dkim?.records?.map((r, idx) => (
          <DnsRow key={idx} record={{ ...r, type: 'CNAME' }} label={`DKIM #${idx + 1}`} onCopy={onCopy} />
        ))}
        {dns.dmarc && <DnsRow record={dns.dmarc} label="DMARC" onCopy={onCopy} />}
      </div>
    </div>
  );
};

const DnsRow = ({ record, label, onCopy }) => (
  <div className="border border-slate-800 rounded-lg p-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-[10px] text-slate-500">{record.type}</span>
      {record.verified ? (
        <CheckCircle2 className="w-4 h-4 text-green-400" />
      ) : (
        <AlertCircle className="w-4 h-4 text-amber-400" />
      )}
    </div>
    <div className="space-y-1.5">
      <DnsField label="Name" value={record.name} onCopy={onCopy} />
      <DnsField label="Value" value={record.value} onCopy={onCopy} />
    </div>
    {record.note && <p className="text-[11px] text-slate-500 mt-2">{record.note}</p>}
  </div>
);

const DnsField = ({ label, value, onCopy }) => (
  <div className="flex items-center gap-2">
    <span className="text-[11px] text-slate-500 w-12 shrink-0">{label}</span>
    <code className="flex-1 text-xs text-slate-200 bg-slate-950/60 px-2 py-1 rounded font-mono truncate">{value}</code>
    <button onClick={() => onCopy(value)} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
      <Copy className="w-3.5 h-3.5" />
    </button>
  </div>
);

const AddDialog = ({ form, setForm, onClose, onSave, saving }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-md p-6" data-testid="super-domain-add-dialog">
      <h2 className="text-lg font-semibold text-white mb-1">Add Platform Domain</h2>
      <p className="text-xs text-slate-400 mb-5">e.g. opswarex.com — must be a domain you own and can edit DNS for.</p>
      <div className="space-y-4">
        <Field label="Domain *">
          <input
            autoFocus
            className={inp}
            placeholder="opswarex.com"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            data-testid="super-domain-input"
          />
        </Field>
        <Field label="Sending subdomain (optional)">
          <input
            className={inp}
            placeholder="mail"
            value={form.sending_subdomain}
            onChange={(e) => setForm({ ...form, sending_subdomain: e.target.value })}
          />
          <p className="text-[11px] text-slate-500 mt-1">If set, emails will be sent from <code>{form.sending_subdomain || 'mail'}.{form.domain || 'yourdomain.com'}</code></p>
        </Field>
        <Field label="Reply-to email (optional)">
          <input
            type="email"
            className={inp}
            placeholder="support@opswarex.com"
            value={form.reply_to_email}
            onChange={(e) => setForm({ ...form, reply_to_email: e.target.value })}
          />
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
          disabled={saving}
        >Cancel</button>
        <button
          onClick={onSave}
          disabled={saving || !form.domain.trim()}
          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium disabled:opacity-50 flex items-center gap-1.5"
          data-testid="super-domain-save-btn"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add
        </button>
      </div>
    </div>
  </div>
);

const inp = 'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white focus:outline-none focus:border-amber-500/50';
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default SuperDomainAuthPage;
